import { Banner, Card, RangeSlider, Text } from "@shopify/polaris"
import { observer } from "mobx-react"
import { useMemo } from "react"
import { Trans } from "react-i18next"

import { PLAN_LIST_MAP } from "@/constant"
import { PaymentChildrenFeatureItem } from "@/pages/Plan/component/PaymentChildren"
import stores from "@/stores"
import { formatDisplayNumber, formatPlanPrice } from "@/utils/formatNumber"

const UpgradeDiscountBannerText = (props: { package_kind: number }) => {
  const { package_kind } = props

  const content = (() => {
    switch (package_kind) {
      case 2:
        return (
          <Trans i18nKey="UpgradeDiscountBannerEssential">
            <Text variant="bodySm" as="span" fontWeight="semibold">
              limited essential plan offer: 30%OFF
            </Text>{" "}
          </Trans>
        )
      case 3:
        return (
          <Trans i18nKey="UpgradeDiscountBannerProfessional">
            <Text variant="bodySm" as="span" fontWeight="semibold">
              limited professional plan offer: 50%OFF
            </Text>{" "}
          </Trans>
        )
      case 4:
        return (
          <Trans i18nKey="UpgradeDiscountBannerEnterprise">
            <Text variant="bodySm" as="span" fontWeight="semibold">
              limited enterprise plan offer: 30%OFF
            </Text>
          </Trans>
        )
      default:
        return null
    }
  })()

  if (!content) return null

  return (
    <div className="inline-flex">
      <Text variant="bodySm" as="p">
        {content}
      </Text>
    </div>
  )
}

const UpgradePannel = (props: {
  type: number
  package_kind: number
  bannerText?: string
}) => {
  const { type, package_kind, bannerText } = props
  const { PlanStore } = stores

  const planListMap = useMemo(() => PLAN_LIST_MAP(), [])

  const { config } = PlanStore

  const info = PlanStore.getInfoByStep(
    package_kind,
    type,
    PlanStore.stepOfUpgrade,
  )

  if (!config || !info) return null
  const { itemPeriodInfo } = info
  const maxStep = itemPeriodInfo?.length - 1
  const showFreeReturnServiceWidget = package_kind >= 3 && type === 1

  const displayPrice = info.current?.actual_price
  const showDiscount =
    Number(info.current?.origin_price) !== Number(info.current?.actual_price) &&
    info.current?.id !== config.user_pkg?.id &&
    type !== 2
  return (
    <div className="space-y-4">
      {bannerText && <Banner tone="info">{bannerText}</Banner>}
      {showDiscount && [2, 3, 4].includes(package_kind) && (
        <Banner tone="info">
          <UpgradeDiscountBannerText package_kind={package_kind} />
        </Banner>
      )}
      <Card>
        <div className="space-y-3">
          {showDiscount ? (
            <div className="flex items-center gap-1">
              <Text
                variant="bodyMd"
                as="span"
                fontWeight="semibold"
                tone="subdued"
              >
                <span className="line-through">
                  {`$${formatPlanPrice(+info.current?.origin_price)}`}
                </span>
              </Text>
              <span className="inline-flex items-center rounded-lg bg-[#a8d8ff] px-2 py-[2px]">
                <Text variant="bodyMd" as="span" fontWeight="semibold">
                  {`$${formatPlanPrice(+displayPrice)}`}
                </Text>
                <Text variant="bodySm" as="span" tone="subdued">
                  /{t("Month")}
                </Text>
              </span>
            </div>
          ) : (
            <div className="flex items-baseline gap-1">
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                {`$${formatPlanPrice(+displayPrice)}`}
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                /{t("Month")}
              </Text>
            </div>
          )}
          <div>
            <RangeSlider
              label=""
              min={0}
              step={1}
              max={maxStep || 1}
              labelHidden
              output={false}
              value={PlanStore.stepOfUpgrade}
              onChange={(value) => {
                PlanStore.setStepOfUpgrade(value as any)
              }}
            />
          </div>
          <div className="space-y-2">
            <Text variant="bodySm" as="span" tone="subdued">
              {type === 1
                ? t("ReturnsQuotaMonth", {
                    replace: {
                      quota: formatDisplayNumber(info.current?.quota),
                    },
                  })
                : t("ReturnsQuotaYear", {
                    replace: {
                      quota: formatDisplayNumber(info.current?.quota),
                    },
                  })}
            </Text>
          </div>
          <Text variant="bodySm" as="p" tone="subdued">
            {type === 1
              ? `$${t("OnePricePerExtraReturn", {
                  replace: {
                    price: formatPlanPrice(info.current?.one_price),
                  },
                })}`
              : t("UnavailableAfterExceeded")}
          </Text>
          {type === 1 && package_kind >= 2 ? (
            <Text variant="bodySm" as="p" tone="subdued">
              {t("FreeReturnServiceSalesBy4_9")}{" "}
            </Text>
          ) : null}
        </div>
      </Card>

      <Card>
        <div className="space-y-3">
          <Text variant="bodyMd" as="p" fontWeight="medium">
            {planListMap[package_kind]?.additionalText}
          </Text>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {planListMap[package_kind]?.list.map((item) => {
              if (
                item.id === "freeReturnServiceWidget" &&
                !showFreeReturnServiceWidget
              ) {
                return null
              }
              return (
                <PaymentChildrenFeatureItem
                  key={item.text}
                  text={item.text}
                  tooltipContent={item.tooltipContent}
                  withoutFeature={item.withoutFeature}
                />
              )
            })}
          </div>
        </div>
      </Card>
    </div>
  )
}
export default observer(UpgradePannel)
